// src/pages/AdminSignIn.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Lock,
  Mail,
  Eye,
  EyeOff,
  ShieldCheck,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
} from "lucide-react";
import { authLogin } from "../api/Landlords";
import { useNavigate } from "react-router-dom";
import Loader from "../components/Loader";

// ✅ NEW: Session-expired toast
import SessionToast from "../components/SessionToast";
import { usePostAuthNotice } from "../hooks/usePostAuthNotice";

export default function AdminSignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false); // used for loader overlay
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [caps, setCaps] = useState(false);
  const navigate = useNavigate();

  const emailRef = useRef(null);
  const pwRef = useRef(null);

  // 🔔 Read one-time notice set by admin 401/403 redirect
  const { notice, clear } = usePostAuthNotice();

  useEffect(() => {
    const saved = localStorage.getItem("admin_last_email");
    if (saved) setEmail(saved);
  }, []);

  const emailValid = useMemo(() => /.+@.+\..+/.test(email.trim()), [email]);
  const pwValid = useMemo(() => password.trim().length >= 8, [password]);
  const canSubmit = emailValid && pwValid && !loading;

  const onKeyUp = (e) => {
    if (e.getModifierState) setCaps(e.getModifierState("CapsLock"));
  };

  // ---------- Friendly error text (only change) ----------
  function normalizeServerError(err) {
    // Prefer server message if present
    const raw =
      err?.response?.data?.error?.detail ||
      err?.response?.data?.detail ||
      err?.response?.data?.message ||
      err?.message ||
      "Unable to sign in. Please try again.";

    // De-duplicate "Authentication failed:" cascades
    let msg = raw.replace(
      /(Authentication failed:\s*)+/gi,
      "Authentication failed: "
    );

    // Extract machine codes if any
    const code =
      err?.response?.data?.error?.code ||
      err?.response?.data?.code ||
      (/_[A-Z]+/.test(raw) ? raw.match(/[A-Z_]{6,}/)?.[0] : "");

    // Map common codes/patterns to friendlier text
    const codeMap = {
      INVALID_LOGIN_CREDENTIALS: "Email or password is incorrect.",
      INVALID_CREDENTIALS: "Email or password is incorrect.",
      USER_NOT_FOUND: "We couldn’t find an account with that email.",
      ACCOUNT_LOCKED: "Your account is locked. Contact an administrator.",
      ACCOUNT_DISABLED: "Your account is disabled. Contact an administrator.",
      OTP_REQUIRED:
        "Additional verification is required. Please check your email.",
    };

    if (code && codeMap[code]) {
      return codeMap[code];
    }

    // If message still technical, make it concise but informative
    if (/invalid[_\s-]*login[_\s-]*credentials/i.test(msg)) {
      return "Email or password is incorrect.";
    }

    // Trim long JSON-ish payloads
    if (msg.length > 240) msg = msg.slice(0, 240) + "…";

    return msg;
  }
  // -------------------------------------------------------

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      await authLogin(email.trim(), password);
      if (remember) localStorage.setItem("admin_last_email", email.trim());
      setSuccess("Signed in successfully");
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(normalizeServerError(err)); // ✅ friendlier error text
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative mx-auto max-w-7xl sm:px-6 lg:px-8 
      pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] 
      py-8 sm:py-12 lg:py-16 mt-10 sm:mt-0"
    >
      {/* 🔔 Session toast shown if redirected due to session expiry */}
      <SessionToast notice={notice} onClose={clear} autoHideMs={6000} />

      {/* 🔄 Full-screen Loader overlay */}
      {loading && (
        <div className="fixed inset-0 z-[9999] grid place-items-center bg-white/80 backdrop-blur-sm">
          <Loader />
        </div>
      )}

      <div className="relative mx-auto max-w-7xl px-10 sm:px-6 lg:px-8 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] py-8 sm:py-12 lg:py-16">
        {/* Soft blobs */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 [mask-image:radial-gradient(60%_60%_at_50%_0%,#000,transparent_70%)]"
        >
          <div className="absolute -top-10 right-10 h-40 w-40 rounded-full blur-3xl bg-blue-400/25" />
          <div className="absolute top-24 -left-10 h-32 w-32 rounded-full blur-2xl bg-violet-400/20" />
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* Hero (lg+) */}
          <div className="hidden lg:block">
            <div className="rounded-3xl border bg-white/70 shadow-xl backdrop-blur p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-300 grid place-items-center text-white shadow-lg">
                  🏢
                </div>
                <div>
                  <div className="text-2xl font-semibold">Admin Console</div>
                  <div className="text-sm text-gray-500">
                    Secure access • Audit-ready
                  </div>
                </div>
              </div>
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <ShieldCheck className="h-4 w-4 mt-0.5 text-blue-600" />
                  <span>
                    Multi-factor authentication (MFA) for stronger security.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <ShieldCheck className="h-4 w-4 mt-0.5 text-blue-600" />
                  <span>
                    Session hardening with device & geo insights (BYO backend).
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <ShieldCheck className="h-4 w-4 mt-0.5 text-blue-600" />
                  <span>Granular roles with least-privilege defaults.</span>
                </li>
              </ul>
              <div className="mt-8 h-44 rounded-2xl bg-gradient-to-br from-blue-500/20 to-violet-500/20 border" />
            </div>
          </div>

          {/* Form */}
          <div className="w-full">
            <div className="mx-auto w-full max-w-md rounded-3xl border bg-white/80 shadow-xl backdrop-blur p-5 sm:p-7">
              <div className="text-center">
                <div className="mx-auto mb-3 h-12 w-12 rounded-2xl bg-gradient-to-br from-blue-600 to-violet-600 grid place-items-center text-white shadow-lg">
                  🔐
                </div>
                <h1 className="text-xl sm:text-2xl font-semibold">
                  Admin · Sign in
                </h1>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                  Use your work email to continue
                </p>
              </div>

              <form
                onSubmit={submit}
                className="mt-6 space-y-4"
                aria-busy={loading}
              >
                {/* Email */}
                <div>
                  <label className="text-sm font-medium">Email</label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      ref={emailRef}
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      aria-invalid={!emailValid}
                      className="w-full rounded-xl border px-9 py-2 outline-none focus:ring-2 focus:ring-blue-200"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Password</label>
                  </div>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      ref={pwRef}
                      type={showPw ? "text" : "password"}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyUp={onKeyUp}
                      aria-invalid={!pwValid}
                      className="w-full rounded-xl border px-9 py-2 pr-10 outline-none focus:ring-2 focus:ring-blue-200"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((s) => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
                      aria-label={showPw ? "Hide password" : "Show password"}
                    >
                      {showPw ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {caps && (
                    <div className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 inline-flex items-center gap-1">
                      <AlertCircle className="h-3.5 w-3.5" /> Caps lock is ON
                    </div>
                  )}
                </div>

                {/* Options */}
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-gray-400">
                    Min 8 characters
                  </span>
                </div>

                {/* Actions */}
                <div className="grid grid-cols-1 gap-2">
                  <button
                    type="submit"
                    disabled={!canSubmit}
                    aria-busy={loading}
                    className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow hover:shadow-md disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Signing in…
                      </>
                    ) : (
                      <>
                        Continue <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>

                {/* Alerts */}
                {(error || success) && (
                  <div className="mt-1" aria-live="polite">
                    {error && (
                      <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" /> {error}
                      </div>
                    )}
                    {success && (
                      <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-xl px-3 py-2 flex items-center gap-2 mt-2">
                        <CheckCircle2 className="h-4 w-4" /> {success}
                      </div>
                    )}
                  </div>
                )}
              </form>

              <p className="mt-6 text-center text-[11px] text-gray-500">
                Protected by modern authentication. By continuing you agree to
                our terms.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
